<script setup lang="ts">
  import pluralize from 'pluralize';
  import { computed } from 'vue';

  import type { Email, EmailThread } from '@stuut/api/models';
  import { humanize } from '@stuut/common/src/utils';
  import { Separator } from '@ui/components/separator';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';

  import TaskReplyWarning from '@/components/tasks/TaskReplyWarning.vue';
  import { HIGH_ALERT_DAY_THRESHOLD } from '@/constants/constants';

  interface Props {
    email?: Email;
    highAlertDayThreshold?: number;
    thread?: EmailThread;
  }

  const props = withDefaults(defineProps<Props>(), {
    highAlertDayThreshold: HIGH_ALERT_DAY_THRESHOLD,
  });

  const emailOrThread = computed(() => props.thread || props.email);

  const daysElapsed = computed(() => emailOrThread.value?.oldestOpenResponseTask?.daysElapsed ?? 0);

  const indicatorStateClass = computed(() =>
    emailOrThread.value?.openTasks?.length
      ? 'text-foreground'
      : 'bg-background text-muted-foreground',
  );
</script>

<template>
  <Tooltip v-if="emailOrThread?.tasks?.length" :delay-duration="0">
    <TooltipTrigger v-bind="$attrs" as-child>
      <div
        class="flex items-center justify-center text-center size-6 rounded-full border"
        :class="[
          indicatorStateClass,
          emailOrThread?.openTasks?.length ? 'border bg-background' : 'border-border bg-muted',
        ]"
      >
        <div v-if="emailOrThread?.tasks?.length" class="relative">
          <i
            class="fas text-muted-foreground text-xs"
            :class="
              emailOrThread?.openTasks?.length
                ? 'text-muted-foreground fa-list-check'
                : 'text-success fa-check'
            "
          ></i>

          <div
            class="absolute size-2 rounded-full z-10 -top-0.5 -right-[7px]"
            :class="daysElapsed >= highAlertDayThreshold && 'bg-error'"
          ></div>
        </div>
      </div>
    </TooltipTrigger>

    <TooltipContent side="left">
      <div class="space-y-1">
        <p class="first-letter:uppercase">
          <template v-if="emailOrThread.openTasks?.length">
            {{ humanize(emailOrThread.object) }} contains
            {{ pluralize('open task', emailOrThread.openTasks?.length, true) }}
          </template>

          <template v-else> All tasks closed or completed. </template>
        </p>

        <template v-if="emailOrThread.openResponseTasks?.length">
          <Separator />

          <div class="flex items-center gap-x-1.5 text-xs">
            <i class="fa fas fa-reply text-agent text-xs!" />

            <strong>
              {{ pluralize('task', emailOrThread.openResponseTasks?.length, true) }}
              requiring a response
            </strong>
          </div>

          <TaskReplyWarning :task="emailOrThread.oldestOpenResponseTask" />
        </template>

        <template v-else>
          <template
            v-if="emailOrThread.completedTasks?.length || emailOrThread.closedTasks?.length"
          >
            <Separator />
          </template>

          <div
            v-if="emailOrThread.completedTasks?.length"
            class="flex items-center gap-x-1.5 text-xs"
          >
            <i class="fa far fa-check-circle text-success text-xs!" />
            {{ emailOrThread.completedTasks?.length }} completed
          </div>

          <div v-if="emailOrThread.closedTasks?.length" class="flex items-center gap-x-1.5 text-xs">
            <i class="fa far fa-circle-x text-muted-foreground text-xs!" />
            {{ emailOrThread.closedTasks?.length }} dismissed
          </div>
        </template>
      </div>
    </TooltipContent>
  </Tooltip>
</template>
